/**
 * PendingReviewQueue — instructor's deferred AI review tasks.
 *
 * Feb 8, 2026 — surfaces /api/lesson-review-requests/instructor/pending
 * as a focused task list. Used by:
 *   • Instructor dashboard (compact banner mode)
 *   • /lesson-review page (full card mode)
 *   • Work Mode end-of-lesson flow (full card mode)
 *
 * Strict rules:
 *   • Pupil-paid + instructor-self deferred requests both render
 *     here (the backend's /instructor/pending already mixes them).
 *   • "Upload footage" routes to /lesson-review?request_id={id} —
 *     the existing upload pipeline auto-links via request_id, no
 *     extra wiring needed.
 *   • "Cancel request" calls /cancel — the backend rule allows
 *     creator OR assigned instructor (so both buttons work).
 *   • Empty state renders nothing in `compact` mode; an empty
 *     "all clear" tile in `full` mode.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Clock, ChevronRight, X, AlertCircle } from "lucide-react";
import { AiReviewTierBadge } from "./AiReviewTierPicker";
import useVisibilityRefetch from "../../hooks/useVisibilityRefetch";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const FOCUS_LABELS = {
  roundabouts: "Roundabouts",
  junctions: "Junctions",
  speed_control: "Speed control",
  mirrors: "Mirrors",
  general_driving: "General driving",
  other: "Other",
};

function _fmtLessonDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short",
    });
  } catch {
    return iso;
  }
}

function _fmtCreatedAgo(iso) {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const days = Math.floor(ms / 86400_000);
    if (days >= 1) return `queued ${days} day${days === 1 ? "" : "s"} ago`;
    const hours = Math.floor(ms / 3_600_000);
    if (hours >= 1) return `queued ${hours} hour${hours === 1 ? "" : "s"} ago`;
    return "queued just now";
  } catch {
    return null;
  }
}

/**
 * @param {object} props
 * @param {"compact"|"full"} [props.variant="full"]
 * @param {(opts: {requestId: string}) => void} [props.onUpload]
 *   - if provided, replaces the default /lesson-review?request_id= navigation
 * @param {(count: number) => void} [props.onCount]
 *   - fires on every refresh with the current pending count
 */
export default function PendingReviewQueue({
  variant = "full",
  onUpload,
  onCount,
  testIdPrefix = "lr-pending-queue",
}) {
  const [items, setItems] = useState(null);   // null = first-load
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    if (!MEDIA_FEATURE_ENABLED) {
      setItems([]);
      if (typeof onCount === "function") onCount(0);
      return;
    }
    try {
      const res = await axios.get(
        `${API}/api/lesson-review-requests/instructor/pending`,
        { withCredentials: true },
      );
      const list = res.data?.items || [];
      setItems(list);
      if (typeof onCount === "function") onCount(list.length);
    } catch (err) {
      // Silent on 401/403 — non-instructors will hit this when the
      // page mounts; nothing to surface.
      const code = err?.response?.status;
      if (code !== 401 && code !== 403) {
        if (typeof console !== "undefined" && console.warn) {
          console.warn("[pending-queue] refresh failed:", err?.message);
        }
      }
      setItems([]);
      if (typeof onCount === "function") onCount(0);
    }
  }, [onCount]);

  useEffect(() => {
    refresh();
    // Best-effort soft-refresh every 60s while mounted — picks up
    // fresh pupil-paid requests without a manual reload.
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  // P1 mobile sprint — close iOS Safari "stale on return" gap.
  useVisibilityRefetch(refresh, { marker: "pending-review-queue" });

  const handleUpload = (req) => {
    if (typeof onUpload === "function") {
      onUpload({ requestId: req.id });
      return;
    }
    // Default — soft navigation; the existing /lesson-review page
    // reads ?request_id= and pre-populates the upload form.
    const qs = new URLSearchParams({ request_id: req.id }).toString();
    window.location.assign(`/lesson-review?${qs}`);
  };

  const handleCancel = async (req) => {
    if (busyId) return;
    if (!window.confirm(
      `Cancel the AI review task for ${req.pupil_name || "this lesson"}? This cannot be undone.`,
    )) return;
    setBusyId(req.id);
    try {
      await axios.post(
        `${API}/api/lesson-review-requests/${req.id}/cancel`, {},
        { withCredentials: true },
      );
      toast.success("Task cancelled.", { id: "lr-pending-cancel" });
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Cancel failed";
      toast.error(detail, { id: "lr-pending-cancel-err" });
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) return null;     // hide until first load resolves

  // Media quarantine — silent. Empty list above already null-renders,
  // but defend the explicit early-return as a doctrine pin.
  if (!MEDIA_FEATURE_ENABLED) return null;

  if (variant === "compact") {
    if (items.length === 0) return null;
    return (
      <button
        type="button"
        data-testid={`${testIdPrefix}-banner`}
        onClick={() => window.location.assign("/lesson-review#pending")}
        className="w-full flex items-center gap-3 rounded-2xl border border-amber-400/40 bg-amber-500/[0.08] hover:bg-amber-500/[0.13] px-4 py-3 text-left transition"
      >
        <Clock className="w-5 h-5 text-amber-300 flex-none" />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold text-amber-100">
            {items.length} AI review{items.length === 1 ? "" : "s"} waiting for upload
          </p>
          <p className="text-[11px] text-amber-200/80 mt-0.5">
            Upload from your laptop when convenient.
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-amber-300 flex-none" />
      </button>
    );
  }

  // Full card mode — task list. Empty state shows a soft "all clear".
  if (items.length === 0) {
    return (
      <div
        data-testid={`${testIdPrefix}-empty`}
        className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5 text-center"
      >
        <p className="text-[12px] text-zinc-400">No AI reviews waiting for upload.</p>
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-list`} className="space-y-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-300">
        <Clock className="w-3 h-3" /> AI reviews waiting for upload · {items.length}
      </div>
      {items.map((req) => {
        const focus = FOCUS_LABELS[req.focus_area] || req.focus_area;
        const lessonDate = _fmtLessonDate(req.lesson_date);
        const queuedAgo = _fmtCreatedAgo(req.created_at);
        const requestedBy = req.created_by_role === "instructor_self"
          ? "Self-queued"
          : (req.created_by_role === "pupil" ? "Pupil request" : "Pupil request");
        return (
          <div
            key={req.id}
            data-testid={`${testIdPrefix}-item-${req.id}`}
            className="rounded-2xl border border-amber-400/30 bg-amber-500/[0.04] p-4"
          >
            <div className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-amber-300 flex-none mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[13px] font-bold text-amber-100 truncate">
                    {req.pupil_name || "Lesson review"}
                  </p>
                  <AiReviewTierBadge tier={req.ai_review_tier} />
                </div>
                <div className="text-[11px] text-amber-200/85 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                  {lessonDate && <span>{lessonDate}</span>}
                  {focus && <span>· {focus}</span>}
                  {queuedAgo && <span>· {queuedAgo}</span>}
                  <span>· {requestedBy}</span>
                </div>
                {req.created_by_role === "instructor_self" && (
                  <p className="text-[10.5px] text-amber-200/60 mt-1.5 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 flex-none" />
                    Auto-cancels after 7 days if untouched.
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                data-testid={`${testIdPrefix}-upload-${req.id}`}
                onClick={() => handleUpload(req)}
                disabled={busyId === req.id}
                className="h-9 px-4 rounded-md bg-amber-400 hover:bg-amber-300 text-[#050505] text-[12px] font-extrabold disabled:opacity-50"
              >
                Upload footage
              </Button>
              <Button
                data-testid={`${testIdPrefix}-cancel-${req.id}`}
                onClick={() => handleCancel(req)}
                disabled={busyId === req.id}
                className="h-9 px-3 rounded-md bg-white/10 hover:bg-white/15 text-zinc-200 text-[12px] font-semibold disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Cancel request
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
